import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { fetchColaborador } from '@/features/rh/api'
import { formatDate, formatMoney } from '@/features/rh/utils'
import { Alert, Avatar, Muted, PageHeader, Section } from '@/components/ui'

type NotifResumo = {
  id?: string
  titulo?: string
  mensagem?: string
  lida?: boolean
  created_at?: string
  [key: string]: unknown
}

async function fetchMinhasNotificacoes() {
  return api.get<{
    sucesso?: boolean
    dados?: NotifResumo[]
    data?: NotifResumo[]
    nao_lidas?: number
    total?: number
  }>('/notificacoes/')
}

export function PortalPage() {
  const { user, perfilLabel } = useAuth()
  const colabId = user?.colaborador_id ? String(user.colaborador_id) : ''

  const ficha = useQuery({
    queryKey: ['portal', 'ficha', colabId],
    queryFn: () => fetchColaborador(colabId),
    enabled: Boolean(colabId),
  })

  const notifs = useQuery({
    queryKey: ['portal', 'notificacoes'],
    queryFn: fetchMinhasNotificacoes,
  })

  const listaNotifs = notifs.data?.dados || notifs.data?.data || []
  const foto = ficha.data?.foto_url || ficha.data?.foto || user?.foto_url || user?.foto

  return (
    <section className="hi-page">
      <PageHeader
        title="Meu painel"
        subtitle="Resumo pessoal autenticado. Holerites, férias e documentos detalhados continuam no portal legado."
        actions={
          <>
            <a className="btn btn-primary" href={legacyAppUrl('#/portal')}>
              Portal legado
            </a>
            <a className="btn btn-secondary" href={legacyAppUrl('#/jornada')}>
              Jornada
            </a>
          </>
        }
      />

      <div className="card hi-profile-head">
        <Avatar src={foto ? String(foto) : null} nome={user?.nome} iniciais={user?.iniciais} size="lg" />
        <div>
          <h4>{user?.nome || 'Colaborador'}</h4>
          <p>
            {perfilLabel}
            {ficha.data?.cargo_nome ? ` · ${ficha.data.cargo_nome}` : ''}
            {ficha.data?.departamento_nome ? ` · ${ficha.data.departamento_nome}` : ''}
          </p>
          <p>{user?.email}</p>
        </div>
      </div>

      {!colabId ? (
        <Alert tone="info">
          Esta conta ainda não está vinculada a um colaborador (<code>colaborador_id</code>). Use o
          legado para jornada completa ou peça o vínculo ao RH.
        </Alert>
      ) : null}

      {ficha.isError ? (
        <Alert tone="danger">
          {ficha.error instanceof ApiError ? ficha.error.message : 'Não foi possível carregar a ficha.'}
        </Alert>
      ) : null}

      {ficha.data ? (
        <div className="hi-fields">
          <Field label="Status" value={String(ficha.data.status_display || ficha.data.status || '-')} />
          <Field label="Matrícula" value={ficha.data.matricula || '-'} />
          <Field label="Admissão" value={formatDate(ficha.data.data_admissao)} />
          <Field label="Tempo de empresa" value={String(ficha.data.tempo_empresa || '-')} />
          <Field label="Regime" value={(ficha.data.regime || '-').toUpperCase()} />
          <Field label="Salário base" value={formatMoney(ficha.data.salario_base)} />
        </div>
      ) : null}

      <Section
        title={`Notificações${notifs.data?.nao_lidas ? ` · ${notifs.data.nao_lidas} não lidas` : ''}`}
      >
        {notifs.isLoading ? <Muted>Carregando…</Muted> : null}
        {notifs.isError ? <Muted>Notificações indisponíveis neste momento.</Muted> : null}
        {listaNotifs.length === 0 && !notifs.isLoading ? (
          <Muted>Nenhuma notificação recente.</Muted>
        ) : (
          <ul className="hi-feed">
            {listaNotifs.slice(0, 8).map((n, i) => (
              <li key={String(n.id || i)}>
                <strong>{n.titulo || 'Aviso'}</strong>
                <span>
                  {n.mensagem || '-'}
                  {n.created_at ? ` · ${formatDate(n.created_at)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="hi-actions">
        <Link className="btn btn-secondary" to="/carreiras">
          Carreiras públicas
        </Link>
        {colabId && (
          <Link className="btn btn-secondary" to={`/app/rh/${colabId}`}>
            Ver ficha RH
          </Link>
        )}
      </div>
    </section>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="hi-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

export default PortalPage
